import React from 'react';

/**
 * SVG Topographic Brain Map
 * Shows activity levels across brain regions from Task B onset localization.
 */

const REGION_POSITIONS = {
  frontal_left:    { cx: 130, cy: 100, label: 'L Frontal' },
  frontal_right:   { cx: 270, cy: 100, label: 'R Frontal' },
  temporal_left:   { cx: 70,  cy: 180, label: 'L Temporal' },
  temporal_right:  { cx: 330, cy: 180, label: 'R Temporal' },
  central:         { cx: 200, cy: 160, label: 'Central' },
  parietal_left:   { cx: 140, cy: 240, label: 'L Parietal' },
  parietal_right:  { cx: 260, cy: 240, label: 'R Parietal' },
  occipital_left:  { cx: 160, cy: 310, label: 'L Occipital' },
  occipital_right: { cx: 240, cy: 310, label: 'R Occipital' },
};

function getColor(value) {
  // 0 = blue/cool, 0.5 = green, 1 = red/hot
  if (value < 0.25) return `rgb(${Math.round(value * 4 * 100)}, ${Math.round(value * 4 * 200 + 55)}, 255)`;
  if (value < 0.5) return `rgb(0, ${Math.round(200 + (value - 0.25) * 4 * 55)}, ${Math.round(255 - (value - 0.25) * 4 * 200)})`;
  if (value < 0.75) return `rgb(${Math.round((value - 0.5) * 4 * 255)}, 255, ${Math.round(55 - (value - 0.5) * 4 * 55)})`;
  return `rgb(255, ${Math.round(255 - (value - 0.75) * 4 * 200)}, 0)`;
}

export default function BrainMap({ data }) {
  if (!data || !data.regions) {
    return <div style={{ color: 'var(--text-muted)', padding: 20, textAlign: 'center' }}>
      Brain map requires Task B (Onset Localization) results
    </div>;
  }

  const { regions, dominant_region } = data;
  const maxVal = Math.max(...Object.values(regions), 0.01);

  return (
    <div className="brain-map-container">
      <div>
        <svg width="400" height="400" viewBox="0 0 400 400">
          {/* Head outline */}
          <ellipse cx="200" cy="200" rx="170" ry="190"
            fill="none" stroke="#475569" strokeWidth="2" />
          {/* Nose indicator */}
          <polygon points="200,15 190,40 210,40" fill="#475569" />
          {/* Ears */}
          <ellipse cx="30" cy="200" rx="12" ry="30" fill="none" stroke="#475569" strokeWidth="1.5" />
          <ellipse cx="370" cy="200" rx="12" ry="30" fill="none" stroke="#475569" strokeWidth="1.5" />

          {/* Midlines */}
          <line x1="200" y1="30" x2="200" y2="380" stroke="#334155" strokeWidth="0.5" strokeDasharray="4 4" />
          <line x1="40" y1="200" x2="360" y2="200" stroke="#334155" strokeWidth="0.5" strokeDasharray="4 4" />

          {/* Regions */}
          {Object.entries(REGION_POSITIONS).map(([key, { cx, cy, label }]) => {
            const val = regions[key] || 0;
            const normalized = val / maxVal;
            const color = getColor(normalized);
            const isDominant = key === dominant_region;
            const radius = 28 + normalized * 15;

            return (
              <g key={key}>
                <circle
                  cx={cx} cy={cy} r={radius}
                  fill={color} fillOpacity={0.6}
                  stroke={isDominant ? '#f59e0b' : '#64748b'}
                  strokeWidth={isDominant ? 3 : 1}
                />
                <text x={cx} y={cy - 6} textAnchor="middle"
                  fill="white" fontSize="10" fontWeight="600">
                  {label}
                </text>
                <text x={cx} y={cy + 10} textAnchor="middle"
                  fill="white" fontSize="11" fontWeight="700">
                  {(val * 100).toFixed(1)}%
                </text>
              </g>
            );
          })}
        </svg>

        {/* Legend */}
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
            Seizure Onset Probability Map
          </div>
          {dominant_region && (
            <div style={{ fontSize: '0.9rem' }}>
              Dominant region: <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                {REGION_POSITIONS[dominant_region]?.label || dominant_region}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 10 }}>
            {[0, 0.25, 0.5, 0.75, 1].map((v) => (
              <div key={v} style={{
                width: 30, height: 12, background: getColor(v), borderRadius: 2
              }} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: 160, margin: '4px auto', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
            <span>Low</span><span>High</span>
          </div>
        </div>
      </div>
    </div>
  );
}
